import { createClient, isMfaChallenge } from '@orbitneststudio/node'
import type { OrbitNestClient } from '@orbitneststudio/node'

const ANON_KEY = process.env.NEXT_PUBLIC_ORBITNEST_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJyb2xlIjoiYW5vbiIsInByb2plY3Rfc2x1ZyI6Im94aWdlbmFwcCIsInBlcm1pc3Npb25zIjp7InJlYWQiOnRydWUsImluc2VydCI6dHJ1ZSwidXBkYXRlIjpmYWxzZSwiZGVsZXRlIjpmYWxzZX0sImlhdCI6MTc3ODc3MTY0Nn0.5zki0Xn6EQvz3OicyfrXugV32V1FKYvDEdpKWECLSSw'
const SERVICE_KEY = process.env.ORBITNEST_SERVICE_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJyb2xlIjoic2VydmljZV9yb2xlIiwicHJvamVjdF9zbHVnIjoib3hpZ2VuYXBwIiwicGVybWlzc2lvbnMiOnsicmVhZCI6dHJ1ZSwiaW5zZXJ0Ijp0cnVlLCJ1cGRhdGUiOnRydWUsImRlbGV0ZSI6dHJ1ZSwiYWRtaW4iOnRydWUsImJ5cGFzc19ybHMiOnRydWV9LCJpYXQiOjE3Nzg3NzE2NDZ9.Re-9qziOctQmTubYLPByIaYCeVx6cvUvX9_5CTpoySE'

export type QueryFilter = Record<string, string | number | boolean | null | { $notNull: true }>

export interface TableQueryOptions {
  select?: string
  filters?: QueryFilter
  limit?: number
  offset?: number
  order?: string
  ascending?: boolean
}

export interface OrbitNestUser {
  id: string
  email: string
  user_metadata?: Record<string, any>
  app_metadata?: Record<string, any>
  created_at?: string
}

export interface AuthResponse {
  user: OrbitNestUser
  token: string
  refresh_token?: string
  expires_at?: number
}

function escapeValue(v: string | number | boolean | null): string {
  if (v === null) return 'NULL'
  if (typeof v === 'boolean') return v ? 'TRUE' : 'FALSE'
  if (typeof v === 'number') return String(v)
  return `'${String(v).replace(/'/g, "''")}'`
}

function buildSelectSql(table: string, options: TableQueryOptions): string {
  let sql = `SELECT ${options.select || '*'} FROM ${table}`
  if (options.filters) {
    const conditions = Object.entries(options.filters)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => {
        if (v === null) return `${k} IS NULL`
        if (typeof v === 'object' && v !== null && '$notNull' in v) return `${k} IS NOT NULL`
        return `${k} = ${escapeValue(v as string | number | boolean)}`
      })
    if (conditions.length) sql += ` WHERE ${conditions.join(' AND ')}`
  }
  if (options.order) sql += ` ORDER BY ${options.order} ${options.ascending === false ? 'DESC' : 'ASC'}`
  if (options.limit) sql += ` LIMIT ${options.limit}`
  if (options.offset) sql += ` OFFSET ${options.offset}`
  return sql
}

class OrbitNestCompat {
  readonly sdk: OrbitNestClient

  constructor(sdk: OrbitNestClient) {
    this.sdk = sdk
  }

  get auth() { return this.sdk.auth }
  get db() { return this.sdk.db }
  get storage() { return this.sdk.storage }
  get functions() { return this.sdk.functions }

  /**
   * Invoke an OrbitNest edge function and return its JSON payload.
   * Throws on transport errors or non-2xx responses from the function.
   */
  async invokeFn<T = any>(name: string, payload: Record<string, any>): Promise<T> {
    const res = await this.sdk.functions.invoke<T>(name, { method: 'POST', body: payload })
    if (res.error) throw new Error(res.error.message)
    const status = res.data?.status ?? 200
    const data: any = res.data?.data
    if (status >= 400) {
      throw new Error(data?.message || data?.error || `Edge function ${name} failed (${status})`)
    }
    return data as T
  }

  async select<T = Record<string, any>>(
    table: string,
    options: TableQueryOptions = {},
    _token?: string,
  ): Promise<T[]> {
    const sql = buildSelectSql(table, options)
    const result = await this.sdk.db.query<T>(sql)
    if (result.error) throw new Error(result.error.message)
    return result.data.rows
  }

  async selectOne<T = Record<string, any>>(
    table: string,
    filters: QueryFilter,
    _token?: string,
  ): Promise<T | null> {
    const rows = await this.select<T>(table, { filters, limit: 1 })
    return rows[0] ?? null
  }

  async insert<T = Record<string, any>>(
    table: string,
    data: Record<string, any>,
    _token?: string,
  ): Promise<T> {
    const result = await this.sdk.db.insert<T>(table, data)
    if (result.error) throw new Error(result.error.message)
    return result.data
  }

  async update<T = Record<string, any>>(
    table: string,
    id: string,
    data: Record<string, any>,
    _token?: string,
  ): Promise<T> {
    const result = await this.sdk.db.update<T>(table, id, data)
    if (result.error) throw new Error(result.error.message)
    return result.data
  }

  async upsert<T = Record<string, any>>(
    table: string,
    data: Record<string, any>,
    conflictKey: string,
  ): Promise<T> {
    const cols = Object.keys(data)
    const vals = Object.values(data)
      .map(v => escapeValue(v as string | number | boolean | null))
      .join(', ')
    const updates = cols
      .filter(c => c !== conflictKey)
      .map(c => `${c} = EXCLUDED.${c}`)
      .join(', ')
    const sql = `INSERT INTO ${table} (${cols.join(', ')}) VALUES (${vals})
      ON CONFLICT (${conflictKey}) DO UPDATE SET ${updates}
      RETURNING *`
    const result = await this.sdk.db.query<T>(sql)
    if (result.error) throw new Error(result.error.message)
    return result.data.rows[0] as T
  }

  async delete(table: string, id: string, _token?: string): Promise<void> {
    const result = await this.sdk.db.delete(table, id)
    if (result.error) throw new Error(result.error.message)
  }

  async count(table: string, filters?: QueryFilter): Promise<number> {
    const sql = buildSelectSql(table, { select: 'COUNT(*) as count', filters })
    const result = await this.sdk.db.query<{ count: string }>(sql)
    if (result.error) throw new Error(result.error.message)
    return parseInt(result.data.rows[0]?.count ?? '0', 10)
  }

  async getCurrentUser(token: string): Promise<OrbitNestUser> {
    this.sdk.auth.setSession({
      access_token: token,
      refresh_token: '',
      expires_in: 3600,
      token_type: 'Bearer',
      user: { id: '', email: '', created_at: '', updated_at: '' },
    })
    const result = await this.sdk.auth.getProfile()
    if (result.error) throw new Error(result.error.message)
    const u = result.data
    return { id: u.id, email: u.email, user_metadata: u.user_metadata as Record<string, any>, created_at: u.created_at }
  }

  async signIn(email: string, password: string): Promise<AuthResponse> {
    const result = await this.sdk.auth.signIn({ email, password })
    if (result.error) throw new Error(result.error.message)
    if (isMfaChallenge(result.data)) throw new Error('Multi-factor authentication is required for this account.')
    const s = result.data
    return {
      user: { id: s.user.id, email: s.user.email, user_metadata: s.user.user_metadata, created_at: s.user.created_at },
      token: s.access_token,
      refresh_token: s.refresh_token,
      expires_at: Math.floor((Date.now() + s.expires_in * 1000) / 1000),
    }
  }

  async signOut(token?: string): Promise<void> {
    if (token) {
      this.sdk.auth.setSession({
        access_token: token,
        refresh_token: '',
        expires_in: 0,
        token_type: 'Bearer',
        user: { id: '', email: '', created_at: '', updated_at: '' },
      })
    }
    await this.sdk.auth.signOut()
  }

  async refreshToken(refreshTokenValue: string): Promise<AuthResponse> {
    this.sdk.auth.setSession({
      access_token: '',
      refresh_token: refreshTokenValue,
      expires_in: 0,
      token_type: 'Bearer',
      user: { id: '', email: '', created_at: '', updated_at: '' },
    })
    const result = await this.sdk.auth.refreshSession()
    if (result.error || !result.data) throw new Error(result.error?.message || 'Refresh failed')
    const s = result.data
    return {
      user: { id: s.user.id, email: s.user.email, user_metadata: s.user.user_metadata, created_at: s.user.created_at },
      token: s.access_token,
      refresh_token: s.refresh_token,
      expires_at: Math.floor((Date.now() + s.expires_in * 1000) / 1000),
    }
  }

  /**
   * Idempotently ensure a storage bucket exists. Safe to call on every upload;
   * the underlying createBucket endpoint returns the existing bucket if one is
   * already present rather than throwing.
   */
  async ensureBucket(bucket: string, options?: { public?: boolean }): Promise<void> {
    const result = await this.sdk.storage.from(bucket).createBucket()
    if (result.error) throw new Error(`ensureBucket(${bucket}): ${result.error.message}`)
    if (options?.public !== undefined) {
      // createBucket honours the `public` flag on first creation; for existing
      // buckets we'd need a separate update endpoint, which isn't required for
      // current callers (issue-attachments is private + proxied).
    }
  }

  async uploadFile(
    bucket: string,
    path: string,
    file: File | Blob,
    options?: { upsert?: boolean },
  ): Promise<{ url: string; path: string }> {
    const blob = file instanceof Blob ? file : new Blob([file as any])
    const result = await this.sdk.storage.from(bucket).upload(path, blob, options)
    if (result.error) throw new Error(result.error.message)
    return { url: result.data.url, path: result.data.path }
  }

  getPublicUrl(bucket: string, filePath: string): string {
    return this.sdk.storage.from(bucket).getPublicUrl(filePath).data.publicUrl
  }
}

// Server-side client (service role key — never expose to browser)
export const orbitnest = new OrbitNestCompat(createClient({ apiKey: SERVICE_KEY }))

// Client-side client (anon key — safe for browser)
export const orbitnestPublic = new OrbitNestCompat(createClient({ apiKey: ANON_KEY }))
